import {
  TEntityResponse,
  TEntityListResponse,
  TCreateSceneReqBody,
  TSceneResBody,
  TSceneSummaryResBody,
  TUpdateSceneReqBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TScenesClient = {
  create(body: TCreateSceneReqBody): Promise<TEntityResponse<'scene', TSceneResBody> | undefined>;
  update(id: string, body: TUpdateSceneReqBody): Promise<TEntityResponse<'scene', TSceneResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'scene', TSceneResBody> | undefined>;
  list(limit: number, page: number): Promise<TEntityListResponse<'scene', TSceneSummaryResBody> | undefined>;
  list_filtered(
    limit: number,
    page: number,
    filter: 'area' | 'company',
    id: string
  ): Promise<TEntityListResponse<'scene', TSceneSummaryResBody> | undefined>;
  activate(id: string): Promise<TEntityResponse<'scene', TSceneResBody> | undefined>;
  deactivate(id: string): Promise<TEntityResponse<'scene', TSceneResBody> | undefined>;
};

function ScenesClient(apiClient: TAPIClient): TScenesClient {
  return {
    async create(body: TCreateSceneReqBody): Promise<TEntityResponse<'scene', TSceneResBody> | undefined> {
      const data = await apiClient.post<TCreateSceneReqBody, TEntityResponse<'scene', TSceneResBody>>('/scenes', body);

      if (data && data.type !== 'scene') {
        throw new Error('Invalid entity type received, expected: scene, received: ' + data.type);
      }

      return data;
    },

    async update(id: string, body: TUpdateSceneReqBody): Promise<TEntityResponse<'scene', TSceneResBody> | undefined> {
      const data = await apiClient.put<TUpdateSceneReqBody, TEntityResponse<'scene', TSceneResBody>>(
        `/scenes/${id}`,
        body
      );

      if (data && data.type !== 'scene') {
        throw new Error('Invalid entity type received, expected: scene, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/scenes/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'scene', TSceneResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'scene', TSceneResBody>>(`/scenes/${id}`);

      if (data && data.type !== 'scene') {
        throw new Error('Invalid entity type received, expected: scene, received: ' + data.type);
      }

      return data;
    },

    async list(limit: number, page: number): Promise<TEntityListResponse<'scene', TSceneSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'scene', TSceneSummaryResBody>>(
        `/scenes?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'scene') {
        throw new Error('Invalid entity type received, expected: scene, received: ' + data.type);
      }

      return data;
    },

    async list_filtered(
      limit: number,
      page: number,
      filter: 'area' | 'company',
      id: string
    ): Promise<TEntityListResponse<'scene', TSceneSummaryResBody> | undefined> {
      const selectedFilter = filter === 'area' ? 'area_id' : 'company_id';

      const data = await apiClient.get<TEntityListResponse<'scene', TSceneSummaryResBody>>(
        `/scenes?filter[${selectedFilter}]=${id}&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'scene') {
        throw new Error('Invalid entity type received, expected: scene, received: ' + data.type);
      }

      return data;
    },

    async activate(id: string): Promise<TEntityResponse<'scene', TSceneResBody> | undefined> {
      const data = await apiClient.post<Record<string, never>, TEntityResponse<'scene', TSceneResBody>>(
        `/scenes/${id}/activate`,
        {}
      );

      if (data && data.type !== 'scene') {
        throw new Error('Invalid entity type received, expected: scene, received: ' + data.type);
      }

      return data;
    },

    async deactivate(id: string): Promise<TEntityResponse<'scene', TSceneResBody> | undefined> {
      const data = await apiClient.post<Record<string, never>, TEntityResponse<'scene', TSceneResBody>>(
        `/scenes/${id}/deactivate`,
        {}
      );

      if (data && data.type !== 'scene') {
        throw new Error('Invalid entity type received, expected: scene, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TScenesClient, ScenesClient };
